<template>
  <v-container style="max-width: 600px">
    <v-btn
      flat
      class="no-background mb-4"
      data-cy="payment-cancel"
      prependIcon="mdi-keyboard-backspace"
      @click="router.push({ name: 'profile' })"
    >
      {{ $t("views.payment.cancel") }}
    </v-btn>
    <h2 class="mb-2">{{ $t("views.payment.invoiceTitle") }}</h2>
    <p v-if="amount" class="mb-4">
      {{ $t("views.payment.amountDue") }}: <strong>{{ amount }}</strong>
    </p>
    <v-alert v-if="error" type="error" class="mb-4" data-cy="payment-error">{{ error }}</v-alert>
    <v-progress-linear v-if="loading" indeterminate />
    <div ref="payment" class="mb-4" data-cy="payment-element"></div>
    <v-btn
      v-if="elements"
      color="primary"
      data-cy="invoice-pay-now"
      :loading="submitting"
      :disabled="submitting"
      @click="submit"
    >
      {{ $t("views.payment.payNow") }}
    </v-btn>
  </v-container>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref, shallowRef, useTemplateRef } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useTranslation } from "i18next-vue";
import axios from "axios";
import { getStripe } from "@/utils/stripe";

const route = useRoute();
const router = useRouter();
const { t, i18next } = useTranslation();

const paymentElementRef = useTemplateRef("payment");
const loading = ref(true);
const submitting = ref(false);
const error = ref(null);
const amount = ref(null);
const elements = shallowRef(null);

let stripe = null;
let paymentElement = null;

onMounted(async () => {
  try {
    const response = await axios.get(
      "/api/stripe/invoice-payment/" + route.params.invoice_id
    );

    if (response.data.status === "paid") {
      router.replace({ name: "profile" });
      return;
    }

    const { client_secret, amount_due, currency } = response.data;
    amount.value = new Intl.NumberFormat(i18next.language, {
      style: "currency",
      currency: currency.toUpperCase()
    }).format(amount_due / 100);

    stripe = await getStripe();
    elements.value = stripe.elements({ clientSecret: client_secret });
    paymentElement = elements.value.create("payment");
    paymentElement.mount(paymentElementRef.value);
  } catch (err) {
    console.error(err);
    error.value = t("views.payment.loadError");
  } finally {
    loading.value = false;
  }
});

onBeforeUnmount(() => {
  paymentElement?.destroy();
});

async function submit() {
  submitting.value = true;
  error.value = null;

  // Cards don't need a redirect, so stay in the SPA and only let Stripe
  // redirect for payment methods that require it (e.g. Klarna)
  const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
    elements: elements.value,
    redirect: "if_required",
    confirmParams: {
      return_url:
        window.location.origin +
        "/payment/invoice-complete/" +
        route.params.licence_id +
        "/" +
        route.params.invoice_id
    }
  });

  if (!confirmError && paymentIntent) {
    router.replace({
      name: "payment-invoice-complete",
      params: {
        licence_id: route.params.licence_id,
        invoice_id: route.params.invoice_id
      },
      query: { redirect_status: paymentIntent.status }
    });
    return;
  }

  // Only reached if confirmation fails
  error.value = confirmError?.message ?? t("views.payment.paymentFailed");
  submitting.value = false;
}
</script>
